import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { WizardData, CreatedDocument, DocCategory, DOC_CATEGORIES } from './types';
import { createInvoice, createReceipt, getVatRate } from '../../api/client';

interface ReviewStepProps {
  data: WizardData;
  onCommentsChange: (comments: string) => void;
  onSuccess: (doc: CreatedDocument) => void;
}

const CATEGORY_BADGE_STYLES: Record<DocCategory, string> = {
  invoice: 'bg-blue-100 text-blue-700 border-blue-300',
  receipt: 'bg-green-100 text-green-700 border-green-300',
  delivery_note: 'bg-orange-100 text-orange-700 border-orange-300',
};

export default function ReviewStep({ data, onCommentsChange, onSuccess }: ReviewStepProps) {
  const { t } = useTranslation();
  const [vatRate, setVatRate] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getVatRate()
      .then((res) => {
        if (!cancelled) {
          const rate = res.data?.vat_rate ?? res.data?.rate ?? res.data;
          setVatRate(typeof rate === 'number' ? rate : null);
        }
      })
      .catch(() => {
        // VAT rate is non-critical; continue without it
      });
    return () => { cancelled = true; };
  }, []);

  const categoryConfig = DOC_CATEGORIES.find((c) => c.id === data.category);
  const categoryLabel = categoryConfig ? t(categoryConfig.titleKey) : '';

  const subtotal = data.category === 'receipt'
    ? data.payments.reduce((sum, p) => sum + p.amount, 0)
    : data.items.reduce((sum, item) => sum + item.quantity * item.price, 0);

  const vatAmount = vatRate != null ? subtotal * (vatRate / 100) : 0;
  const total = data.priceIncludeVat ? subtotal : subtotal + vatAmount;

  const handleCreate = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let res;

      if (data.category === 'receipt') {
        const payload = {
          receipt_type: data.receiptType,
          customer_id: data.customer?.customer_id || 0,
          payments: data.payments.map((p) => ({
            payment_type: p.payment_type,
            amount_nis: p.amount,
          })),
          comments: data.comments || undefined,
          send_mail: false,
        };
        res = await createReceipt(payload);
      } else {
        const payload = {
          document_type: data.docType,
          customer_id: data.customer?.customer_id || 0,
          items: data.items.map((item) => ({
            item_id: item.item_id || undefined,
            catalog_number: item.catalog_number || undefined,
            quantity: item.quantity,
            description: item.description,
            price_nis: item.price,
          })),
          comments: data.comments || undefined,
          price_include_vat: data.priceIncludeVat,
          send_mail: false,
        };
        res = await createInvoice(payload);
      }

      onSuccess(res.data as CreatedDocument);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('createDocument.review.error');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [data, onSuccess, t]);

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 space-y-6">
        {/* Document Type Badge */}
        {data.category && (
          <div className="flex items-center gap-3">
            <span className="text-2xl">{categoryConfig?.icon}</span>
            <span
              className={`inline-block rounded-full border px-4 py-1 text-sm font-semibold ${CATEGORY_BADGE_STYLES[data.category]}`}
            >
              {categoryLabel}
            </span>
          </div>
        )}

        {/* Customer */}
        {data.customer && (
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
              {t('createDocument.review.customer')}
            </p>
            <p className="mt-1 text-lg font-semibold text-gray-900">
              {data.customer.display_name}
            </p>
          </div>
        )}

        {/* Items Table (invoice / delivery_note) */}
        {data.category !== 'receipt' && data.items.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
              {t('createDocument.review.items')}
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500">
                    <th className="px-4 py-2 text-start font-medium">
                      {t('createDocument.items.description')}
                    </th>
                    <th className="px-4 py-2 text-center font-medium">
                      {t('createDocument.items.qty')}
                    </th>
                    <th className="px-4 py-2 text-end font-medium">
                      {t('createDocument.items.price')}
                    </th>
                    <th className="px-4 py-2 text-end font-medium">
                      {t('createDocument.items.lineTotal')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item, idx) => (
                    <tr
                      key={item.id}
                      className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                    >
                      <td className="px-4 py-2 text-gray-800">{item.description}</td>
                      <td className="px-4 py-2 text-center text-gray-600">
                        {item.quantity}
                      </td>
                      <td className="px-4 py-2 text-end text-gray-600">
                        ₪{Number(item.price).toFixed(2)}
                      </td>
                      <td className="px-4 py-2 text-end font-medium text-gray-800">
                        ₪{Number(item.quantity * item.price).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payments Table (receipt only) */}
        {data.category === 'receipt' && data.payments.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
              {t('createDocument.review.payments')}
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-500">
                    <th className="px-4 py-2 text-start font-medium">
                      {t('createDocument.payments.title')}
                    </th>
                    <th className="px-4 py-2 text-end font-medium">
                      {t('createDocument.payments.amount')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.payments.map((p, idx) => (
                    <tr
                      key={idx}
                      className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                    >
                      <td className="px-4 py-2 text-gray-800">{p.payment_name}</td>
                      <td className="px-4 py-2 text-end text-gray-600">
                        ₪{Number(p.amount).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Totals */}
        <div className="space-y-2 border-t border-gray-100 pt-4">
          <div className="flex justify-between text-sm text-gray-600">
            <span>{t('createDocument.review.subtotal')}</span>
            <span>₪{Number(subtotal).toFixed(2)}</span>
          </div>
          {vatRate != null && (
            <div className="flex justify-between text-sm text-gray-500">
              <span>
                {t('createDocument.review.vat')} ({vatRate}%)
              </span>
              <span>
                {data.priceIncludeVat
                  ? t('createDocument.review.vat')
                  : `₪${Number(vatAmount).toFixed(2)}`}
              </span>
            </div>
          )}
          <div className="flex justify-between text-base font-bold text-gray-900">
            <span>{t('createDocument.review.total')}</span>
            <span>₪{Number(total).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Comments */}
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          {t('createDocument.review.comments')}
        </label>
        <textarea
          value={data.comments}
          onChange={(e) => onCommentsChange(e.target.value)}
          placeholder={t('createDocument.review.commentsPlaceholder')}
          rows={3}
          className="w-full rounded-xl border border-gray-300 p-3 text-sm text-gray-800 placeholder-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
        />
      </div>

      {/* Error Banner */}
      {error && (
        <div className="flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm font-medium text-red-700">{error}</p>
          <button
            type="button"
            onClick={handleCreate}
            className="rounded-lg bg-red-100 px-3 py-1 text-sm font-medium text-red-700 hover:bg-red-200 transition-colors"
          >
            {t('createDocument.review.tryAgain')}
          </button>
        </div>
      )}

      {/* Create Button */}
      <button
        type="button"
        onClick={handleCreate}
        disabled={loading}
        className={[
          'w-full rounded-xl py-4 text-lg font-semibold transition-colors',
          loading
            ? 'cursor-not-allowed bg-blue-400 text-white'
            : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800',
        ].join(' ')}
      >
        {loading ? (
          <span className="inline-flex items-center gap-2">
            <svg
              className="h-5 w-5 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            {t('createDocument.review.creating')}
          </span>
        ) : (
          t('createDocument.review.create')
        )}
      </button>
    </div>
  );
}
